import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import moment from "moment-timezone";
import ResourceSelect from "components/shared/inputs/ResourceSelect";
import Dialog from "components/shared/Dialog";
import Button from "components/shared/Button";
import BaseForm, { Field } from "components/shared/Form";
import ResponseTable, { type TransferResult } from "./ResponseTable";
import { useFilters } from "utils/hooks/useFilters";
import { useTransferMutation } from "../../../api/useTransferMutation";
import { formatTimeSheetParams } from "../../../../../TimeSheet/api/helpers";
import { renderUserName } from "utils/helpers/user";

const getTransferUserSearchFilters = (search: string) => ({
    search,
    withoutLookups: "true",
});

function createTransferSchema({ t }: { t: (key: string) => string }) {
    return z
        .object({
            user: z.any().optional()
        })
        .superRefine((formValues, ctx) => {
            if (!formValues.user?.id) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["user"],
                    message: t("required-user"),
                });
            }
        });
}

type TransferFormValues = z.infer<ReturnType<typeof createTransferSchema>>;

function Transfer({ visible, values, close }: any) {
    const { t } = useTranslation();
    const [filters] = useFilters();

    const schema = useMemo(() => createTransferSchema({ t }), [t]);

    const form = useForm<TransferFormValues>({
        resolver: zodResolver(schema)
    });

    const [results, setResults] = useState<TransferResult[] | null>(null);

    const cancel = useCallback(() => {
        form.reset();
        setResults(null);
        close();
    }, [close, form]);

    const onAfterSuccess = useCallback((results: TransferResult[]) => {
        form.reset();
        if (results.length) setResults(results);
    }, [form]);

    const { submitMutation } = useTransferMutation({
        values,
        close: cancel,
        onAfterSuccess,
    });

    const onFinish = useCallback((value: TransferFormValues) => {
        const data: any = { newUserId: value?.user?.id, user: value?.user, isTemplate: values?.isTemplate };

        if (values?.type === "row") {
            const from = filters?.mode === "month" ? moment(filters?.from).startOf("week").format("YYYY-MM-DD") : filters?.from;
            const to = filters?.mode === "month" ? moment(filters?.to).endOf("week").format("YYYY-MM-DD") : filters?.to;
            data.from = from;
            data.to = to;
            data.filters = {
                pageIndex: Number(filters?.page) - 1,
                pageSize: filters?.pageSize,
                ...formatTimeSheetParams({
                    filters: filters?.filters,
                    from,
                    to
                }),
                sort: filters?.sort
            };
            if (values?.isTemplate && filters?.mode === "day") {
                data.filters.day = filters?.day;
            }
        }

        submitMutation.mutate(data);
    }, [filters, values, submitMutation]);

    return (
        <Dialog
            title={values?.type === "row"
                ? t("transfer-events")
                : t("transfer-event")
            }
            open={visible}
            close={cancel}
            size={results ? "4xl" : undefined}
        >
            {results ? (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-p-5">
                    <ResponseTable data={results} />
                    <div className="tw-flex tw-justify-end">
                        <Button type="button" onClick={cancel}>
                            {t("close")}
                        </Button>
                    </div>
                </div>
            ) : (
                <BaseForm form={form} onFinish={onFinish} className="tw-p-5">
                    <Field name="user" required>
                        <ResourceSelect
                            resourcePath="/users"
                            renderLabel={renderUserName}
                            placeholder={t("users")}
                            mode="single"
                            showSearch
                            getSearchFilter={getTransferUserSearchFilters}
                        />
                    </Field>

                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={cancel}
                            disabled={submitMutation.isPending}
                            loading={submitMutation.isPending}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            disabled={submitMutation.isPending}
                            loading={submitMutation.isPending}
                        >
                            {t("transfer")}
                        </Button>
                    </div>
                </BaseForm>
            )}
        </Dialog>
    );
}

export default Transfer;
